/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { AuthorisationApiConfiguration } from '../authorisation-api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ListUsersResponse as ApiListUsersResponse } from '../models/api/list-users-response';
import { MeResponseV1 as ApiMeResponseV1 } from '../models/api/me-response-v-1';
import { MeResponseV2 as ApiMeResponseV2 } from '../models/api/me-response-v-2';
import { User as ApiUser } from '../models/api/user';
import { createBoardUser } from '../fn/users/create-board-user';
import { CreateBoardUser$Params } from '../fn/users/create-board-user';
import { createTenantUser } from '../fn/users/create-tenant-user';
import { CreateTenantUser$Params } from '../fn/users/create-tenant-user';
import { getMe } from '../fn/users/get-me';
import { GetMe$Params } from '../fn/users/get-me';
import { getMeV1 } from '../fn/users/get-me-v-1';
import { GetMeV1$Params } from '../fn/users/get-me-v-1';
import { listBoardWatchlistUsers } from '../fn/users/list-board-watchlist-users';
import { ListBoardWatchlistUsers$Params } from '../fn/users/list-board-watchlist-users';
import { listTenantWatchlistUsers } from '../fn/users/list-tenant-watchlist-users';
import { ListTenantWatchlistUsers$Params } from '../fn/users/list-tenant-watchlist-users';
import { searchBoardUsers } from '../fn/users/search-board-users';
import { SearchBoardUsers$Params } from '../fn/users/search-board-users';
import { searchTenantUsers } from '../fn/users/search-tenant-users';
import { SearchTenantUsers$Params } from '../fn/users/search-tenant-users';
import { setActiveState } from '../fn/users/set-active-state';
import { SetActiveState$Params } from '../fn/users/set-active-state';

@Injectable({ providedIn: 'root' })
export class UsersService extends BaseService {
  constructor(config: AuthorisationApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `setActiveState()` */
  static readonly SetActiveStatePath = '/v1/users/active-state';

  /**
   * Patch active state, tenant and board.
   *
   * Sets the active state a user has, setting active tenant and board ids
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `setActiveState()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setActiveState$Response(params: SetActiveState$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return setActiveState(this.http, this.rootUrl, params, context);
  }

  /**
   * Patch active state, tenant and board.
   *
   * Sets the active state a user has, setting active tenant and board ids
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `setActiveState$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  setActiveState(params: SetActiveState$Params, context?: HttpContext): Observable<void> {
    return this.setActiveState$Response(params, context).pipe(map((r: StrictHttpResponse<void>): void => r.body));
  }

  /** Path part for operation `getMeV1()` */
  static readonly GetMeV1Path = '/v1/users/me';

  /**
   * Get me (V1).
   *
   * Get me returns the user data with service providers and data providers structure
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMeV1()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMeV1$Response(params?: GetMeV1$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiMeResponseV1>> {
    return getMeV1(this.http, this.rootUrl, params, context);
  }

  /**
   * Get me (V1).
   *
   * Get me returns the user data with service providers and data providers structure
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMeV1$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMeV1(params?: GetMeV1$Params, context?: HttpContext): Observable<ApiMeResponseV1> {
    return this.getMeV1$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiMeResponseV1>): ApiMeResponseV1 => r.body)
    );
  }

  /** Path part for operation `searchBoardUsers()` */
  static readonly SearchBoardUsersPath = '/v2/users/board';

  /**
   * Search users by email on board level (V2).
   *
   * Search for users by providing an email query parameter.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchBoardUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  searchBoardUsers$Response(
    params: SearchBoardUsers$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiListUsersResponse>> {
    return searchBoardUsers(this.http, this.rootUrl, params, context);
  }

  /**
   * Search users by email on board level (V2).
   *
   * Search for users by providing an email query parameter.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchBoardUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  searchBoardUsers(params: SearchBoardUsers$Params, context?: HttpContext): Observable<ApiListUsersResponse> {
    return this.searchBoardUsers$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiListUsersResponse>): ApiListUsersResponse => r.body)
    );
  }

  /** Path part for operation `createBoardUser()` */
  static readonly CreateBoardUserPath = '/v2/users/board';

  /**
   * Create or retrieve a user when adding to a board.
   *
   * Create or retrieve a user when adding to a board. Requires BoardManageGrants permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createBoardUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createBoardUser$Response(
    params: CreateBoardUser$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiUser>> {
    return createBoardUser(this.http, this.rootUrl, params, context);
  }

  /**
   * Create or retrieve a user when adding to a board.
   *
   * Create or retrieve a user when adding to a board. Requires BoardManageGrants permission.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createBoardUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createBoardUser(params: CreateBoardUser$Params, context?: HttpContext): Observable<ApiUser> {
    return this.createBoardUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiUser>): ApiUser => r.body)
    );
  }

  /** Path part for operation `listBoardWatchlistUsers()` */
  static readonly ListBoardWatchlistUsersPath = '/v2/users/board/watchlist';

  /**
   * List users for board watchlist (V2).
   *
   * List users for board using V2 API with OpenFGA
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listBoardWatchlistUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  listBoardWatchlistUsers$Response(
    params: ListBoardWatchlistUsers$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiListUsersResponse>> {
    return listBoardWatchlistUsers(this.http, this.rootUrl, params, context);
  }

  /**
   * List users for board watchlist (V2).
   *
   * List users for board using V2 API with OpenFGA
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listBoardWatchlistUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listBoardWatchlistUsers(
    params: ListBoardWatchlistUsers$Params,
    context?: HttpContext
  ): Observable<ApiListUsersResponse> {
    return this.listBoardWatchlistUsers$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiListUsersResponse>): ApiListUsersResponse => r.body)
    );
  }

  /** Path part for operation `getMe()` */
  static readonly GetMePath = '/v2/users/me';

  /**
   * Get me (V2).
   *
   * Get me returns the user data with service providers and data providers structure
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMe()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMe$Response(params?: GetMe$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiMeResponseV2>> {
    return getMe(this.http, this.rootUrl, params, context);
  }

  /**
   * Get me (V2).
   *
   * Get me returns the user data with service providers and data providers structure
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getMe$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMe(params?: GetMe$Params, context?: HttpContext): Observable<ApiMeResponseV2> {
    return this.getMe$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiMeResponseV2>): ApiMeResponseV2 => r.body)
    );
  }

  /** Path part for operation `searchTenantUsers()` */
  static readonly SearchTenantUsersPath = '/v2/users/tenant';

  /**
   * Search tenant users by email on tenant level (V2).
   *
   * Search for users by providing an email query parameter.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchTenantUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  searchTenantUsers$Response(
    params: SearchTenantUsers$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiListUsersResponse>> {
    return searchTenantUsers(this.http, this.rootUrl, params, context);
  }

  /**
   * Search tenant users by email on tenant level (V2).
   *
   * Search for users by providing an email query parameter.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchTenantUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  searchTenantUsers(params: SearchTenantUsers$Params, context?: HttpContext): Observable<ApiListUsersResponse> {
    return this.searchTenantUsers$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiListUsersResponse>): ApiListUsersResponse => r.body)
    );
  }

  /** Path part for operation `createTenantUser()` */
  static readonly CreateTenantUserPath = '/v2/users/tenant';

  /**
   * Create or retrieve a user when adding to a tenant.
   *
   * Create or retrieve a user when adding to a tenant. Requires TenantManageMembers permission.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createTenantUser()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTenantUser$Response(
    params: CreateTenantUser$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiUser>> {
    return createTenantUser(this.http, this.rootUrl, params, context);
  }

  /**
   * Create or retrieve a user when adding to a tenant.
   *
   * Create or retrieve a user when adding to a tenant. Requires TenantManageMembers permission.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createTenantUser$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createTenantUser(params: CreateTenantUser$Params, context?: HttpContext): Observable<ApiUser> {
    return this.createTenantUser$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiUser>): ApiUser => r.body)
    );
  }

  /** Path part for operation `listTenantWatchlistUsers()` */
  static readonly ListTenantWatchlistUsersPath = '/v2/users/tenant/watchlist';

  /**
   * List users for tenant watchlist (V2).
   *
   * List users for tenant using V2 API with OpenFGA
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listTenantWatchlistUsers()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTenantWatchlistUsers$Response(
    params?: ListTenantWatchlistUsers$Params,
    context?: HttpContext
  ): Observable<StrictHttpResponse<ApiListUsersResponse>> {
    return listTenantWatchlistUsers(this.http, this.rootUrl, params, context);
  }

  /**
   * List users for tenant watchlist (V2).
   *
   * List users for tenant using V2 API with OpenFGA
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listTenantWatchlistUsers$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listTenantWatchlistUsers(
    params?: ListTenantWatchlistUsers$Params,
    context?: HttpContext
  ): Observable<ApiListUsersResponse> {
    return this.listTenantWatchlistUsers$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiListUsersResponse>): ApiListUsersResponse => r.body)
    );
  }
}
